/**
 * The app's UiActions and the store-driven app effects — the QA-tuned behaviour every shell needs, shared by the web
 * (apps/client `src/app/boot.ts`) and the native shell:
 *
 *   startMatch           an OFFLINE match started from inside a private room leaves the room first (QA r4: the friend
 *                        must not wait on a ghost member who is off playing a lesson)
 *   rematch              online: offer a rematch to the same opponent; offline: same setup, fresh seed
 *   continueFromResults  online: decline the rematch and go back to the lobby; offline: the main menu
 *   quitMatch            back to the menu that started the match (lobby / main menu)
 *   effects              the persisted bindings + touch-controls policy follow the settings; the Versus splash warms
 *                        the match renderer up for the pending setup and any other screen but the match drops it;
 *                        leaving Results closes the replay theater
 *
 * Platform-neutral (signals-core effects, injected scene / service handles).
 */
import { effect } from '@preact/signals-core';
import { randomSeed } from '@hf/shared';
import { trackRematchTapped } from '../analytics/tracker';
import type { TouchMode } from '../input/types';
import type { ModeCommand } from '../modes/types';
import type { UiActions } from './actions';
import type { UiStore } from './store';
import type { MatchSetup, ScreenId } from './types';

export interface AppControllerDeps {
  ui: UiStore;
  /** Screen changes (SceneManager.goto). */
  goto(screen: ScreenId): void;
  /** The lobby client (only `leaveRoom` is needed). */
  online: { leaveRoom(): void };
  /** Online rematch handshakes (OnlineFlow). */
  onlineFlow: { rematch(): void; declineRematch(): void };
  /** The match scene: in-match commands and the renderer warm-up for the Versus splash. */
  matchScene: { command(c: ModeCommand): void; prepare(setup: MatchSetup): void; dropPrepared(): void };
  /** The setup the Versus splash is about to start (ui-model/flow `pendingMatch`), if any. */
  pendingSetup(): MatchSetup | null;
  /** Instant-replay theater on the Results screen (closed when leaving it). */
  replays?: { closeTheater(): void } | null;
  /** Device bindings + touch-controls policy (InputManager). */
  input: { loadBindings(saved: Readonly<Record<string, unknown>>): void; setTouchMode(m: TouchMode): void };
  /** Audio unlock from a user gesture. */
  unlockAudio(): void;
  /** Fresh match seed (tests inject). */
  seed?: () => number;
}

export interface AppController {
  readonly actions: UiActions;
  /** Stop the effects. */
  dispose(): void;
}

export const createAppController = (d: AppControllerDeps): AppController => {
  const { ui } = d;
  const seed = d.seed ?? randomSeed;
  const disposers: (() => void)[] = [];

  // Persisted rebinding + touch-controls policy follow the settings (sanitized inside the input manager).
  disposers.push(
    effect(() => {
      const { bindings, touchControls } = ui.settings.value;
      d.input.loadBindings(bindings);
      d.input.setTouchMode(touchControls);
    }),
  );
  // The Versus splash warms the match renderer up (context, art, shader programs) so kickoff is instant; leaving the
  // splash for a menu frees it. Leaving Results closes its replay theater.
  disposers.push(
    effect(() => {
      const screen = ui.screen.value;
      if (screen === 'versus') {
        const setup = d.pendingSetup() ?? ui.matchSetup.value;
        if (setup) d.matchScene.prepare(setup);
      } else if (screen !== 'match') d.matchScene.dropPrepared();
      if (screen !== 'results') d.replays?.closeTheater();
    }),
  );

  const actions: UiActions = {
    goto: (screen) => d.goto(screen),
    startMatch: (setup) => {
      // An offline match (Results → PRACTICE IT after a private-room game) leaves the room first: the friend must
      // not wait on a ghost member who is off playing a lesson (QA r4). Nothing else starts an offline match
      // from inside a room (the lobby's exit leaves it).
      if (!setup.online && ui.net.peek().room) d.online.leaveRoom();
      ui.matchSetup.value = setup;
      d.goto('match');
    },
    quitMatch: () => d.goto(ui.matchSetup.value?.online ? 'online_lobby' : 'main_menu'),
    pause: () => {
      if (ui.screen.value === 'match' && ui.overlay.value === 'none') ui.overlay.value = 'pause';
    },
    resume: () => {
      if (ui.paused.value) ui.overlay.value = 'none';
    },
    rematch: () => {
      const setup = ui.matchSetup.value;
      if (!setup) return;
      trackRematchTapped(ui);
      if (setup.online) {
        // Same opponent, same transport: match_found{rematch: true} → onMatch → Versus → a new session.
        d.onlineFlow.rematch();
        return;
      }
      actions.startMatch({ ...setup, config: { ...setup.config, seed: seed() } });
    },
    continueFromResults: () => {
      ui.results.value = null;
      if (ui.matchSetup.value?.online) {
        d.onlineFlow.declineRematch();
        d.goto('online_lobby');
        return;
      }
      d.goto('main_menu');
    },
    unlockAudio: () => d.unlockAudio(),
    practiceReset: () => d.matchScene.command('reset'),
    practiceSwapSides: () => d.matchScene.command('swap_sides'),
    tutorialSkip: () => d.matchScene.command('skip'),
    tutorialRestart: () => d.matchScene.command('restart'),
  };

  return {
    actions,
    dispose: () => {
      for (const f of disposers.splice(0)) f();
    },
  };
};
